/**
 * Eén labelregistratie als kaartje (MA-61): op het Kanban-bord in de kolom
 * "Nieuwe labels", links van de fasen. Een adres dat al in de vangst staat
 * opent de woning op de kaart; een onbekend adres gaat naar een zoekopdracht
 * op het web, want dat huis staat nog nergens. Afkeuren haalt het label voor
 * iedereen weg, op het bord en op de kaart.
 */
import {
  energielabelKleur,
  formatDatum,
  formatOppervlakte,
  type LabelfeedRij,
} from '@mamimap/shared';
import { ExternalLink, Search, X } from 'lucide-react';
import { useKeurLabelAf } from '@/lib/api/queries';
import { Link } from '@/lib/router';
import { dagenGeleden, woningTekst, woningUrl, zoeklink } from './labelfeed';

/** Het label als gekleurd blokje, met het vorige label ervoor als dat bekend is. */
export function KlasseChip({ rij }: { rij: Pick<LabelfeedRij, 'energieklasse' | 'vorigeKlasse'> }) {
  if (rij.energieklasse === null) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[11px]">
      {rij.vorigeKlasse ? (
        <>
          <span
            className="px-1 font-medium text-ink opacity-60"
            style={{ backgroundColor: energielabelKleur(rij.vorigeKlasse) }}
          >
            {rij.vorigeKlasse}
          </span>
          <span aria-hidden="true" className="text-faint">
            →
          </span>
        </>
      ) : null}
      <span
        className="px-1 font-medium text-ink"
        style={{ backgroundColor: energielabelKleur(rij.energieklasse) }}
      >
        {rij.energieklasse}
      </span>
    </span>
  );
}

export function LabelKaartje({ rij, vandaag }: { rij: LabelfeedRij; vandaag?: Date }) {
  const geleden = dagenGeleden(rij.dag, vandaag);
  const afkeuren = useKeurLabelAf();
  return (
    <article className="border border-line bg-surface-strong px-2.5 py-2 text-[12px]">
      <div className="flex items-start justify-between gap-2">
        {rij.woning ? (
          <Link
            naar={woningUrl(rij.woning.id)}
            className="text-left font-medium text-ink hover:text-accent"
          >
            {rij.adres}
          </Link>
        ) : (
          <span className="font-medium text-ink">{rij.adres}</span>
        )}
        <a
          href={zoeklink(rij.adres)}
          target="_blank"
          rel="noreferrer noopener"
          className="mt-0.5 shrink-0 text-faint hover:text-accent"
          aria-label={`${rij.adres} zoeken op het web`}
          title="Zoeken op het web"
        >
          {rij.woning ? (
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <Search className="h-3.5 w-3.5" aria-hidden="true" />
          )}
        </a>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-ink-soft">
        <KlasseChip rij={rij} />
        {rij.oppervlakteM2 === null ? null : (
          <span className="cijfers">{formatOppervlakte(Math.round(rij.oppervlakteM2))}</span>
        )}
        {rij.bouwjaar === null ? null : <span className="cijfers">{rij.bouwjaar}</span>}
        {rij.buurtnaam ? <span className="truncate">{rij.buurtnaam}</span> : null}
      </div>
      <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-faint">
        <span className="cijfers">
          {geleden === 0 ? 'vandaag' : geleden === 1 ? 'gisteren' : `${geleden} dagen geleden`}
          {rij.opnamedatum ? ` · opname ${formatDatum(rij.opnamedatum)}` : ''}
        </span>
        <span className={rij.woning ? 'text-accent' : undefined}>
          {woningTekst(rij, formatDatum)}
        </span>
      </div>
      <div className="mt-1.5 flex justify-end">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            afkeuren.mutate(rij.id);
          }}
          disabled={afkeuren.isPending}
          className="inline-flex items-center gap-1 text-[11px] text-faint hover:text-ink disabled:opacity-50"
          title="Dit label niet meer tonen, ook niet bij de ander"
        >
          <X className="h-3 w-3" aria-hidden="true" />
          Afkeuren
        </button>
      </div>
    </article>
  );
}
